import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { pipelineApi } from '../services/pipelineApi';
import type { PipelineFormData, PipelineStagePayload } from '../types/crm.types';

// Query keys factory
export const pipelineKeys = {
  all: ['pipelines'] as const,
  lists: () => [...pipelineKeys.all, 'list'] as const,
  list: (filters: any) => [...pipelineKeys.lists(), filters] as const,
  details: () => [...pipelineKeys.all, 'detail'] as const,
  detail: (id: string) => [...pipelineKeys.details(), id] as const,
};

/**
 * Hook to fetch all pipelines
 */
export function usePipelines() {
  return useQuery({
    queryKey: pipelineKeys.all,
    queryFn: () => pipelineApi.getAll(),
  });
}

/**
 * Hook to fetch single pipeline by ID
 */
export function usePipeline(id: string) {
  return useQuery({
    queryKey: pipelineKeys.detail(id),
    queryFn: () => pipelineApi.getById(id),
    enabled: !!id,
  });
}

/**
 * Hook to create pipeline
 */
export function useCreatePipeline() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: PipelineFormData) => pipelineApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pipelineKeys.all });
    },
  });
}

/**
 * Hook to update pipeline
 */
export function useUpdatePipeline() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<PipelineFormData> }) =>
      pipelineApi.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: pipelineKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: pipelineKeys.all });
    },
  });
}

/**
 * Hook to delete pipeline
 */
export function useDeletePipeline() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => pipelineApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pipelineKeys.all });
    },
  });
}

export function useAddStage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ pipelineId, stageData }: { pipelineId: string; stageData: PipelineStagePayload }) =>
      pipelineApi.addStage(pipelineId, stageData),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: pipelineKeys.detail(variables.pipelineId) });
    },
  });
}

export function useUpdateStage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ pipelineId, stageId, stageData }: { pipelineId: string; stageId: string; stageData: Partial<PipelineStagePayload> }) =>
      pipelineApi.updateStage(pipelineId, stageId, stageData),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: pipelineKeys.detail(variables.pipelineId) });
    },
  });
}

export function useDeleteStage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ pipelineId, stageId }: { pipelineId: string; stageId: string }) =>
      pipelineApi.deleteStage(pipelineId, stageId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: pipelineKeys.detail(variables.pipelineId) });
    },
  });
}

export function useReorderStages() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ pipelineId, stageOrder }: { pipelineId: string; stageOrder: string[] }) =>
      pipelineApi.reorderStages(pipelineId, stageOrder),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: pipelineKeys.detail(variables.pipelineId) });
    },
  });
}
